import React from "react"
import { Link } from "react-router-dom"
import {Zap, Battery, Gauge, Shield, ArrowRight} from 'lucide-react'

const HomePage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 md:py-32">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
              <Zap size={18} className="text-orange-500" />
              <span className="text-sm font-semibold text-orange-400">Professional Generator Automation</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              The Ultimate <span className="text-orange-500">Generator Auto Start</span> & Monitoring Solutions
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Upgrade your system with a reliable <span className="text-orange-400 font-semibold">remote start generator</span> kit for RV, Home, and Solar applications.
            </p>
            
            <p className="text-lg text-gray-400 mb-10 max-w-3xl mx-auto">
              Whether you need an <span className="font-semibold text-white">autostart generator</span> for emergency backup or convenience, we have the controllers and automation solutions you need.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/solar-backup-power"
                className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Solar Solutions
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <Link
                to="/portable-generator"
                className="inline-flex items-center justify-center px-8 py-4 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200"
              >
                Portable Kits
                <ArrowRight size={20} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Complete Automation Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional-grade components for every application
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Battery className="text-orange-500" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Solar Integration</h3>
              <p className="text-gray-600">
                Seamless integration with Victron, Growatt, and other solar inverters for automatic battery charging.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="text-orange-500" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Remote Start</h3>
              <p className="text-gray-600">
                Add wireless remote start capability to any portable or standby generator system.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Gauge className="text-orange-500" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Smart Monitoring</h3>
              <p className="text-gray-600">
                Real-time monitoring of voltage, runtime, and system status from anywhere.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="text-orange-500" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Reliable Protection</h3>
              <p className="text-gray-600">
                Built-in safety features prevent damage from low oil, overheating, and voltage issues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Solar Solution */}
            <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-200">
              <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <Battery className="text-white" size={32} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Auto Start Generators for Solar Backup Power
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Integrate automatic generator starting with your solar system. When battery voltage drops below your threshold, the generator automatically starts to recharge your battery bank. Perfect for off-grid and backup power systems.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span className="text-gray-700">Compatible with Victron, Growatt inverters</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span className="text-gray-700">2-wire start integration</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span className="text-gray-700">Automatic battery charging cycles</span>
                </li>
              </ul>
              <Link
                to="/solar-backup-power"
                className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-700 transition-colors"
              >
                Learn More About Solar Integration
                <ArrowRight size={20} className="ml-2" />
              </Link>
            </div>

            {/* Portable Solution */}
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-200">
              <div className="w-16 h-16 bg-gray-700 rounded-xl flex items-center justify-center mb-6">
                <Zap className="text-white" size={32} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Remote Start Portable Generator Kits
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Add remote start capability to portable generators that didn't come with factory automation. Perfect for RV, camping, and mobile power applications with Honda, Predator, and Westinghouse units.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span className="text-gray-700">Works with major portable brands</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span className="text-gray-700">Wireless remote operation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span className="text-gray-700">Easy DIY installation</span>
                </li>
              </ul>
              <Link
                to="/portable-generator"
                className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-700 transition-colors"
              >
                Explore Portable Solutions
                <ArrowRight size={20} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Automate Your Generator?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Explore our comprehensive guides and find the perfect solution for your needs
          </p>
          <Link
            to="/guides"
            className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Browse Installation Guides
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default HomePage
